import { Activity, ActivityForm } from '../types/Activity';

const API_BASE_URL = `${process.env.REACT_APP_TRAVEL_API_BASE_URL}`;

export interface ActivitySearchParams {
  city?: string;
  city_code?: string;
  cityId?: number;
  limit?: number;
  offset?: number;
}

export const fetchAllActivities = (params: { 
  page?: number; 
  limit?: number; 
  city?: string; 
  city_code?: string;
  category?: string; 
  is_active?: boolean;
} = {}) => {
  const searchParams = new URLSearchParams();
  
  if (params.page) searchParams.append('page', params.page.toString());
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.city) searchParams.append('city', params.city);
  if (params.city_code) searchParams.append('city_code', params.city_code);
  if (params.category) searchParams.append('category', params.category);
  if (params.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());

  const url = `${API_BASE_URL}/activities?${searchParams.toString()}`;
  
  return fetch(url, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  })
    .then(res => {
      if (!res.ok) throw new Error(`Failed to fetch activities: ${res.status}`);
      return res.json();
    })
    .then(data => {
      return data;
    });
};

export const fetchActivityById = (id: string) => {
  return fetch(`${API_BASE_URL}/activities/${id}`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  })
    .then(res => {
      if (!res.ok) throw new Error(`Failed to fetch activity: ${res.status}`);
      return res.json();
    });
};

export const getActivityFull = (idOrSlug: string) => {
  return fetch(`${API_BASE_URL}/activities/${idOrSlug}/full`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  })
    .then(res => {
      if (!res.ok) throw new Error(`Failed to fetch activity full: ${res.status}`);
      return res.json();
    });
};

export const createActivity = (data: ActivityForm, file?: File) => {
  const token = localStorage.getItem('token');

  if (file) {
    const form = new FormData();

    Object.entries(data).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (k === 'variants') {
        form.append('variants', JSON.stringify(v));
      } else {
        form.append(k, typeof v === 'string' ? v : String(v));
      }
    });

    form.append('picture', file);

    return fetch(`${API_BASE_URL}/activities`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token || ''}`
      },
      body: form
    }).then(res => {
      if (!res.ok) throw new Error(`Failed to create activity: ${res.status}`);
      return res.json();
    });
  }

  return fetch(`${API_BASE_URL}/activities`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token || ''}`,
    },
    body: JSON.stringify(data)
  }).then(res => {
    if (!res.ok) throw new Error(`Failed to create activity: ${res.status}`);
    return res.json();
  });
};

export const updateActivity = (
  id: string,
  data: Partial<ActivityForm>,
  file?: File
) => {
  const token = localStorage.getItem('token') || '';

  if (file) {
    const form = new FormData();

    Object.entries(data).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (k === 'variants') {
        form.append('variants', JSON.stringify(v));
      } else {
        form.append(k, typeof v === 'string' ? v : String(v));
      }
    });

    form.append('picture', file);

    return fetch(`${API_BASE_URL}/activities/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: form,
    }).then(res => {
      if (!res.ok) throw new Error(`Failed to update activity: ${res.status}`);
      return res.json();
    });
  }

  return fetch(`${API_BASE_URL}/activities/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  }).then(res => {
    if (!res.ok) throw new Error(`Failed to update activity: ${res.status}`);
    return res.json();
  });
};


export const deleteActivity = (id: string) => {
  return fetch(`${API_BASE_URL}/activities/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  })
    .then(res => {
      if (!res.ok) throw new Error(`Failed to delete activity: ${res.status}`);
      return res.json();
    });
};

export function fetchActivities(params: ActivitySearchParams = {}): Promise<Activity[]> {
  const searchParams = new URLSearchParams();
  
  if (params.city) {
    searchParams.append('city', params.city);
  }
  
  if (params.city_code) {
    searchParams.append('city_code', params.city_code);
  }
  
  if (params.cityId) {
    searchParams.append('city_id', params.cityId.toString());
  }
  
  if (params.limit) {
    searchParams.append('limit', params.limit.toString());
  }
  
  if (params.offset) {
    searchParams.append('offset', params.offset.toString());
  }

  const url = `${API_BASE_URL}/activities?${searchParams.toString()}`;
  
  return fetch(url)
    .then(res => {
      if (!res.ok) {
        throw new Error(`Failed to fetch activities: ${res.status} ${res.statusText}`);
      }
      return res.json() as Promise<Activity[]>;
    })
    .catch(error => {
      return [];
    });
}

export function fetchActivitiesByCity(city: string, limit: number = 20): Promise<Activity[]> {
  return fetchActivities({ city, limit });
}

export function fetchActivitiesByCityId(cityId: number, limit: number = 20): Promise<Activity[]> {
  return fetchActivities({ cityId, limit });
}

export function fetchActivitiesByCityCode(cityCode: string, limit: number = 20): Promise<Activity[]> {
  return fetchActivities({ city_code: cityCode, limit });
}
